// src/config/menuItems.tsx
import { useEffect, useRef, useState } from 'react';
import * as Lucide from 'lucide-react';
import { apiService } from '@/services/api/apiService'; // same service used by Login

// ---------- API → UI types ----------
export type ApiMenuItem = {
  id: number;
  label: string;
  icon?: string | null; // e.g. "LayoutDashboard"
  path?: string | null; // e.g. "/foodmenu"
  subMenu?: ApiMenuItem[];
  isActive?: boolean;
};

export type MenuItem = {
  name: string;
  icon?: React.ComponentType<any>;
  route?: string;
  submenu?: MenuItem[];
  isActive?: boolean;
};

// ---------- Dynamic icon mapper (no need to list all) ----------
const FallbackIcon = Lucide.CircleHelp;
const DynamicIconMap: Record<string, React.ComponentType<any>> = new Proxy(
  {},
  {
    get: (_t, key: string) => (Lucide as any)[key] ?? FallbackIcon,
  },
);

// ---------- Adapter: DB -> UI ----------
function adapt(items?: ApiMenuItem[]): MenuItem[] {
  if (!Array.isArray(items)) return [];
  return items.map((i) => ({
    name: i.label,
    icon: i.icon ? DynamicIconMap[i.icon] : FallbackIcon,
    route: i.path ?? undefined,
    submenu: adapt(i.subMenu),
    isActive: i.isActive,
  }));
}

// ---------- ENV (same pattern as Login) ----------
const PORT = import.meta.env.VITE_IAM_PORT || '8080';
const IAMURL = import.meta.env.VITE_API_IAM_URL || '/api/v1/tiffit/idam';

// ---------- Hook to fetch menu for a user ----------
export function useMenuItems(authUserId: string) {
  const [items, setItems] = useState<MenuItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const requestRef = useRef(0); // prevent stale setState
  const fetchData = async () => {
    const reqId = ++requestRef.current;
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.post<any>('/get_user_menu', { authUserId }, PORT, IAMURL);
      const payload = (res as any)?.data ?? res; // support axios-like or fetch-like
      const raw: ApiMenuItem[] = Array.isArray(payload) ? payload : (payload.menu ?? []);
      if (reqId === requestRef.current) setItems(adapt(raw));
    } catch (e: any) {
      if (reqId === requestRef.current) setError(e?.message ?? 'Failed to load menu');
    } finally {
      if (reqId === requestRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    if (authUserId) fetchData();
    // no AbortSignal passed to apiService — avoids TS mismatch
    return () => {
      requestRef.current++;
    };
  }, [authUserId]);

  return { items, loading, error, refetch: fetchData };
}

// ---------- Legacy export so old imports don't break ----------
export const menuItems: MenuItem[] = []; // real data now comes from useMenuItems()
export type { MenuItem as SidebarMenuItem };
